import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";
import useAuth from "../hooks/useAuth";
import * as XLSX from "xlsx";

import { useToast } from "../hooks/useToast";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Alert,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { DataGrid } from "@mui/x-data-grid";

import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import SendIcon from "@mui/icons-material/Send";
import DescriptionIcon from "@mui/icons-material/Description";

import { MapContainer, TileLayer, Marker, LayersControl, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

import { FingerprintChecker } from "./fingerprintChecker";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const edificioIcon = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const cierreIcon = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png",
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

export const ListadoIntervenciones = () => {
  const toast = useToast();
  const { auth } = useAuth();
  const navigate = useNavigate();

  const esAdmin = auth?.rol === "admin";
  const esGerente = auth?.rol === "gerente";

  const [ticajes, setTicajes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [centros, setCentros] = useState([]);
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filtroUsuario, setFiltroUsuario] = useState("");
  const [filtroCliente, setFiltroCliente] = useState("");
  const [filtroCentro, setFiltroCentro] = useState("");
  const [filtroOrden, setFiltroOrden] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const [modalEditar, setModalEditar] = useState(false);
  const [ticajeEditar, setTicajeEditar] = useState(null);

  const [editInicio, setEditInicio] = useState("");
  const [editFin, setEditFin] = useState("");
  const [editMotivo, setEditMotivo] = useState("");
  const [editObs, setEditObs] = useState("");
  const [editComentarios, setEditComentarios] = useState("");
  const [editOrdenId, setEditOrdenId] = useState("");
  const [editError, setEditError] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const [mostrarMapa, setMostrarMapa] = useState(false);
  const [mapFocus, setMapFocus] = useState("inicio");
  const [ubicacionInicio, setUbicacionInicio] = useState(null);
  const [ubicacionCierre, setUbicacionCierre] = useState(null);
  const [ubicacionCentro, setUbicacionCentro] = useState(null);

  const [modalEmailOpen, setModalEmailOpen] = useState(false);
  const [selectedTicajeEmail, setSelectedTicajeEmail] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  const parseDateSafe = (value) => {
    if (!value) return null;

    const normalized =
      typeof value === "string" && value.includes(" ") && !value.includes("T")
        ? value.replace(" ", "T")
        : value;

    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const pad = (n) => String(n).padStart(2, "0");

  const fmtDate = (value) => {
    const d = parseDateSafe(value);
    if (!d) return "-";

    return d.toLocaleString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const fmtDateExcel = (value) => {
    const d = parseDateSafe(value);
    if (!d) return "-";

    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
      d.getHours()
    )}:${pad(d.getMinutes())}`;
  };

  const toLocalDateInputString = (value) => {
    const d = parseDateSafe(value);
    if (!d) return "";

    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const toDatetimeLocalString = (dateValue) => {
    const d = parseDateSafe(dateValue);
    if (!d) return "";

    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
      d.getHours()
    )}:${pad(d.getMinutes())}`;
  };

  const calcularDuracion = (ms) => {
    if (ms == null || Number.isNaN(ms)) return "No disponible";

    const horas = Math.floor(ms / 3600000);
    const minutos = Math.floor((ms % 3600000) / 60000);

    return `${horas}h ${minutos}min`;
  };

  const nombreCompleto = (u) =>
    [u?.nombre, u?.apellidos ?? u?.apellido ?? u?.apellidos_usuario]
      .filter(Boolean)
      .join(" ")
      .trim();

  const nombreUsuario = useCallback(
    (id) => {
      const u = usuarios.find((x) => String(x.id) === String(id));
      return u ? nombreCompleto(u) || u.nombre || `Usuario ${id}` : "-";
    },
    [usuarios]
  );

  const nombreOrden = useCallback(
    (id) => {
      const o = ordenes.find((x) => String(x.id) === String(id));
      return o ? o.titulo || `OT ${o.id}` : id ? `OT ${id}` : "-";
    },
    [ordenes]
  );

  const usuariosEquipo = useMemo(() => {
    if (!esGerente) return [];
    return (usuarios || []).filter((u) => String(u.gerente_id) === String(auth?.id));
  }, [usuarios, esGerente, auth?.id]);

  const usuariosFiltrables = useMemo(() => {
    if (esAdmin) return usuarios || [];
    if (esGerente) return usuariosEquipo;
    return [];
  }, [usuarios, usuariosEquipo, esAdmin, esGerente]);

  const isEmailValid = (e) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || "").trim());

  const openSendModal = useCallback((row) => {
    setSelectedTicajeEmail(row || null);

    const prefill =
      (row?.cliente_email || row?.email_cliente || row?.email || "").toString().trim();

    setEmailTo(prefill);
    setModalEmailOpen(true);
  }, []);

  const closeSendModal = () => {
    if (sendingEmail) return;
    setModalEmailOpen(false);
    setSelectedTicajeEmail(null);
    setEmailTo("");
  };

  const enviarParteEmail = async () => {
    const ticajeId = selectedTicajeEmail?.id;
    const email = String(emailTo || "").trim();

    if (!ticajeId) {
      toast.error("No se pudo determinar el ticaje.");
      return;
    }

    if (!isEmailValid(email)) {
      toast.error("Email inválido.");
      return;
    }

    setSendingEmail(true);

    try {
      const token = localStorage.getItem("token");

      const res = await fetch(
        `${Global.url}partes-trabajo/ticaje-finalizado/${ticajeId}/enviar-email`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        }
      );

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        let msg = txt || "No se pudo enviar el parte.";

        try {
          const j = JSON.parse(txt);
          msg = j?.message || j?.error || msg;
        } catch {}

        throw new Error(msg);
      }

      toast.success("Parte enviado por email.");
      closeSendModal();
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Error enviando el parte.");
    } finally {
      setSendingEmail(false);
    }
  };

  const cargarDatos = useCallback(async () => {
    if (!auth?.id) return;

    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [resT, resU, resC, resCentros, resOts] = await Promise.all([
        fetch(`${Global.url}ticajes/finalizados`, { headers }),
        fetch(`${Global.url}usuarios`, { headers }),
        fetch(`${Global.url}clientes`, { headers }),
        fetch(`${Global.url}centros/todos`, { headers }),
        fetch(`${Global.url}ordenes`, { headers }),
      ]);

      const datosT = resT.ok ? await resT.json() : [];
      const datosU = resU.ok ? await resU.json() : [];
      const datosC = resC.ok ? await resC.json() : [];
      const datosCentros = resCentros.ok ? await resCentros.json() : [];
      const datosOts = resOts.ok ? await resOts.json() : [];

      if (!resT.ok) toast.error("No se pudieron cargar las intervenciones finalizadas.");
      if (!resU.ok) toast.warning("No se pudieron cargar usuarios.");
      if (!resC.ok) toast.warning("No se pudieron cargar clientes.");
      if (!resCentros.ok) toast.warning("No se pudieron cargar centros.");
      if (!resOts.ok) toast.warning("No se pudieron cargar OT.");

      setUsuarios(Array.isArray(datosU) ? datosU : []);
      setClientes(Array.isArray(datosC) ? datosC : []);
      setCentros(Array.isArray(datosCentros) ? datosCentros : []);
      setOrdenes(Array.isArray(datosOts) ? datosOts : []);

      const usuariosById = new Map(
        (Array.isArray(datosU) ? datosU : []).map((u) => [String(u.id), u])
      );
      const clientesById = new Map(
        (Array.isArray(datosC) ? datosC : []).map((c) => [String(c.id), c])
      );
      const centrosById = new Map(
        (Array.isArray(datosCentros) ? datosCentros : []).map((c) => [String(c.id), c])
      );
      const ordenesById = new Map(
        (Array.isArray(datosOts) ? datosOts : []).map((o) => [String(o.id), o])
      );

      const nombreUsuarioLocal = (id) => {
        if (!id) return "-";
        const u = usuariosById.get(String(id));
        return u ? nombreCompleto(u) || u.nombre || `Usuario ${id}` : `Usuario ${id}`;
      };

      const nombreClienteLocal = (id) => {
        if (!id) return "-";
        const c = clientesById.get(String(id));
        return c ? c.nombre || `Cliente ${id}` : `Cliente ${id}`;
      };

      const nombreCentroLocal = (id) => {
        if (!id) return "-";
        const c = centrosById.get(String(id));
        return c ? c.nombre || `Centro ${id}` : `Centro ${id}`;
      };

      const nombreOrdenLocal = (id) => {
        if (!id) return "-";
        const o = ordenesById.get(String(id));
        return o ? o.titulo || `OT ${id}` : `OT ${id}`;
      };

      const idsEquipo = new Set(
        (Array.isArray(datosU) ? datosU : [])
          .filter((u) => String(u.gerente_id) === String(auth?.id))
          .map((u) => String(u.id))
      );

      const enriquecidos = (Array.isArray(datosT) ? datosT : [])
        .filter((t) => {
          if (esAdmin) return true;
          if (esGerente) return idsEquipo.has(String(t.usuario_id));
          return false;
        })
        .map((t) => {
          const ini = parseDateSafe(t.inicio)?.getTime() ?? 0;
          const fin = parseDateSafe(t.fin)?.getTime() ?? 0;

          const motivo = (t?.motivo_fin || "").toLowerCase();
          const estado = motivo.includes("cancel")
            ? "Cancelado"
            : motivo.includes("manual")
            ? "Finalizado Manual"
            : "Finalizado";

          const clienteObj = clientesById.get(String(t.cliente_id));

          return {
            ...t,
            estado,
            duracionMs: fin && ini ? fin - ini : null,
            usuario: nombreUsuarioLocal(t.usuario_id),
            gerente_nombre: nombreUsuarioLocal(t.gerente_id),
            modificado_por_nombre: t.creado_por_admin_id
              ? nombreUsuarioLocal(t.creado_por_admin_id)
              : t.creado_por_nombre || "-",
            cliente: t.cliente || nombreClienteLocal(t.cliente_id),
            centro: t.centro || nombreCentroLocal(t.centro_id),
            orden_nombre: t.orden_nombre || nombreOrdenLocal(t.orden_id),
            cliente_email:
              t.cliente_email ||
              t.email_cliente ||
              clienteObj?.email ||
              clienteObj?.correo ||
              clienteObj?.email_contacto ||
              "",
          };
        });

      setTicajes(enriquecidos);
    } catch (err) {
      console.error("Error al cargar datos:", err);
      toast.error("Error al cargar datos iniciales.");
      setTicajes([]);
    } finally {
      setLoading(false);
    }
  }, [auth?.id, esAdmin, esGerente, toast]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const limpiarFiltros = () => {
    setFiltroUsuario("");
    setFiltroCliente("");
    setFiltroCentro("");
    setFiltroOrden("");
    setFechaDesde("");
    setFechaHasta("");
    toast.info("Filtros limpiados.");
  };

  const ticajesFiltrados = useMemo(() => {
    return (ticajes || []).filter((t) => {
      const inicioISO = toLocalDateInputString(t.inicio);

      const matchUsuario = !filtroUsuario || String(t.usuario_id) === String(filtroUsuario);
      const matchCliente = !filtroCliente || String(t.cliente_id) === String(filtroCliente);
      const matchCentro = !filtroCentro || String(t.centro_id) === String(filtroCentro);
      const matchOrden = !filtroOrden || String(t.orden_id) === String(filtroOrden);

      const matchFecha =
        !fechaDesde && !fechaHasta
          ? true
          : fechaDesde && !fechaHasta
          ? inicioISO === fechaDesde
          : fechaDesde && fechaHasta
          ? inicioISO >= fechaDesde && inicioISO <= fechaHasta
          : true;

      return matchUsuario && matchCliente && matchCentro && matchOrden && matchFecha;
    });
  }, [ticajes, filtroUsuario, filtroCliente, filtroCentro, filtroOrden, fechaDesde, fechaHasta]);

  useEffect(() => {
    setPage(0);
  }, [filtroUsuario, filtroCliente, filtroCentro, filtroOrden, fechaDesde, fechaHasta, pageSize]);

  useEffect(() => {
    if (!modalEditar || !ticajeEditar) return;

    setEditInicio(toDatetimeLocalString(ticajeEditar.inicio));
    setEditFin(toDatetimeLocalString(ticajeEditar.fin));
    setEditMotivo(ticajeEditar.motivo_fin || "");
    setEditObs(ticajeEditar.observaciones_admin || ticajeEditar.comentario_admin || "");
    setEditComentarios(ticajeEditar.comentarios || "");
    setEditOrdenId(ticajeEditar.orden_id ? String(ticajeEditar.orden_id) : "");
    setEditError("");
  }, [modalEditar, ticajeEditar]);

  const handleGuardarEdicion = async () => {
    if (!ticajeEditar) return;

    if (!editInicio || !editFin) {
      setEditError("Debes indicar inicio y fin.");
      return;
    }

    if (!editObs.trim()) {
      setEditError("Debes introducir una observación.");
      return;
    }

    const dInicio = new Date(editInicio);
    const dFin = new Date(editFin);

    if (Number.isNaN(dInicio.getTime()) || Number.isNaN(dFin.getTime()) || dFin < dInicio) {
      setEditError("La fecha de fin debe ser posterior al inicio.");
      return;
    }

    setSavingEdit(true);

    try {
      const res = await fetch(`${Global.url}ticajes/modificar/${ticajeEditar.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          inicio: editInicio,
          fin: editFin,
          motivo_fin: editMotivo,
          observaciones_admin: editObs,
          comentarios: editComentarios,
          orden_id: editOrdenId ? Number(editOrdenId) : null,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(data.message || "Error al modificar la intervención.");
        return;
      }

      toast.success("Intervención modificada correctamente.");
      setModalEditar(false);
      setTicajeEditar(null);

      await cargarDatos();
    } catch (e) {
      console.error(e);
      toast.error("Error al conectar con el servidor.");
    } finally {
      setSavingEdit(false);
    }
  };

  const abrirMapa = async (t, focus = "inicio") => {
    setMapFocus(focus);

    const ini =
      t?.latitud && t?.longitud ? { lat: Number(t.latitud), lng: Number(t.longitud) } : null;

    const fin =
      t?.latitud_cierre && t?.longitud_cierre
        ? { lat: Number(t.latitud_cierre), lng: Number(t.longitud_cierre) }
        : null;

    setUbicacionInicio(ini);
    setUbicacionCierre(fin);

    try {
      const res = await fetch(`${Global.url}centros/${t.centro_id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok && data?.latitud && data?.longitud) {
        setUbicacionCentro({
          lat: parseFloat(data.latitud),
          lng: parseFloat(data.longitud),
        });
      } else {
        setUbicacionCentro(null);
      }
    } catch (err) {
      console.error("Error al obtener centro:", err);
      setUbicacionCentro(null);
    }

    if (ini || fin) setMostrarMapa(true);
    else toast.warning("No hay coordenadas para mostrar.");
  };

  const mapCenter = useMemo(() => {
    const pick =
      mapFocus === "cierre"
        ? ubicacionCierre || ubicacionInicio
        : ubicacionInicio || ubicacionCierre;

    return pick ? [pick.lat, pick.lng] : [0, 0];
  }, [mapFocus, ubicacionInicio, ubicacionCierre]);

  const exportarCSVReducido = (datos, nombreArchivo = "intervenciones.csv") => {
    if (!Array.isArray(datos) || datos.length === 0) {
      toast.warning("No hay datos para exportar.");
      return;
    }

    const encabezadosAdmin = [
      "Usuario",
      "OT",
      "Cliente",
      "Centro",
      "Gerente",
      "Inicio",
      "Fin",
      "Duración",
      "Estado",
      "En Rango (Inicio)",
      "Distancia (Inicio m)",
      "En Rango (Cierre)",
      "Distancia (Cierre m)",
      "Comentarios",
      "Modificado por",
      "Observaciones",
    ];

    const encabezadosGerente = [
      "Usuario",
      "OT",
      "Cliente",
      "Centro",
      "Inicio",
      "Fin",
      "Duración",
      "Estado",
      "En Rango (Inicio)",
      "Distancia (Inicio m)",
      "En Rango (Cierre)",
      "Distancia (Cierre m)",
      "Comentarios",
      "Modificado por",
      "Observaciones",
    ];

    const encabezados = esAdmin ? encabezadosAdmin : encabezadosGerente;

    const csvRows = [
      encabezados.join(","),
      ...datos.map((t) => {
        const filaAdmin = [
          t.usuario ?? "",
          t.orden_nombre ?? "",
          t.cliente ?? "",
          t.centro ?? "",
          t.gerente_nombre ?? "-",
          t.inicio ? fmtDateExcel(t.inicio) : "-",
          t.fin ? fmtDateExcel(t.fin) : "-",
          calcularDuracion(t.duracionMs),
          t.estado ?? "",
          t.en_rango ? "En rango" : "Fuera de rango",
          t.distancia_m != null ? t.distancia_m : "",
          t.en_rango_cierre == null ? "" : t.en_rango_cierre ? "En rango" : "Fuera de rango",
          t.distancia_m_cierre != null ? t.distancia_m_cierre : "",
          t.comentarios ?? "",
          t.modificado_por_nombre ?? "-",
          t.observaciones_admin || t.comentario_admin || "-",
        ];

        const filaGerente = [
          t.usuario ?? "",
          t.orden_nombre ?? "",
          t.cliente ?? "",
          t.centro ?? "",
          t.inicio ? fmtDateExcel(t.inicio) : "-",
          t.fin ? fmtDateExcel(t.fin) : "-",
          calcularDuracion(t.duracionMs),
          t.estado ?? "",
          t.en_rango ? "En rango" : "Fuera de rango",
          t.distancia_m != null ? t.distancia_m : "",
          t.en_rango_cierre == null ? "" : t.en_rango_cierre ? "En rango" : "Fuera de rango",
          t.distancia_m_cierre != null ? t.distancia_m_cierre : "",
          t.comentarios ?? "",
          t.modificado_por_nombre ?? "-",
          t.observaciones_admin || t.comentario_admin || "-",
        ];

        const fila = esAdmin ? filaAdmin : filaGerente;

        return fila.map((val) => `"${(val ?? "").toString().replace(/"/g, '""')}"`).join(",");
      }),
    ];

    const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + csvRows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");

    link.setAttribute("href", encodedUri);
    link.setAttribute("download", nombreArchivo);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`CSV generado: ${nombreArchivo}`);
  };

  const exportarExcel = (datos, nombreArchivo = "intervenciones.xlsx") => {
    if (!Array.isArray(datos) || datos.length === 0) {
      toast.warning("No hay datos para exportar.");
      return;
    }

    const datosFormatoExcel = datos.map((t) =>
      esAdmin
        ? {
            Usuario: t.usuario ?? "",
            OT: t.orden_nombre ?? "",
            Cliente: t.cliente ?? "",
            Centro: t.centro ?? "",
            Gerente: t.gerente_nombre ?? "-",
            Inicio: t.inicio ? fmtDateExcel(t.inicio) : "-",
            Fin: t.fin ? fmtDateExcel(t.fin) : "-",
            Duración: calcularDuracion(t.duracionMs),
            Estado: t.estado ?? "",
            "En Rango (Inicio)": t.en_rango ? "En rango" : "Fuera de rango",
            "Distancia (Inicio m)": t.distancia_m != null ? t.distancia_m : "",
            "En Rango (Cierre)":
              t.en_rango_cierre == null ? "" : t.en_rango_cierre ? "En rango" : "Fuera de rango",
            "Distancia (Cierre m)": t.distancia_m_cierre != null ? t.distancia_m_cierre : "",
            Comentarios: t.comentarios ?? "",
            "Modificado por": t.modificado_por_nombre ?? "-",
            Observaciones: t.observaciones_admin || t.comentario_admin || "-",
          }
        : {
            Usuario: t.usuario ?? "",
            OT: t.orden_nombre ?? "",
            Cliente: t.cliente ?? "",
            Centro: t.centro ?? "",
            Inicio: t.inicio ? fmtDateExcel(t.inicio) : "-",
            Fin: t.fin ? fmtDateExcel(t.fin) : "-",
            Duración: calcularDuracion(t.duracionMs),
            Estado: t.estado ?? "",
            "En Rango (Inicio)": t.en_rango ? "En rango" : "Fuera de rango",
            "Distancia (Inicio m)": t.distancia_m != null ? t.distancia_m : "",
            "En Rango (Cierre)":
              t.en_rango_cierre == null ? "" : t.en_rango_cierre ? "En rango" : "Fuera de rango",
            "Distancia (Cierre m)": t.distancia_m_cierre != null ? t.distancia_m_cierre : "",
            Comentarios: t.comentarios ?? "",
            "Modificado por": t.modificado_por_nombre ?? "-",
            Observaciones: t.observaciones_admin || t.comentario_admin || "-",
          }
    );

    const hoja = XLSX.utils.json_to_sheet(datosFormatoExcel);
    const libro = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(libro, hoja, "Intervenciones");
    XLSX.writeFile(libro, nombreArchivo);

    toast.success(`Excel generado: ${nombreArchivo}`);
  };

  const rows = useMemo(
    () =>
      ticajesFiltrados.map((t) => ({
        ...t,
        usuario_txt: t.usuario ?? nombreUsuario(t.usuario_id),
        orden_txt: t.orden_nombre ?? nombreOrden(t.orden_id),
        duracion_txt: calcularDuracion(t.duracionMs),
        inicio_txt: t.inicio ? fmtDate(t.inicio) : "-",
        fin_txt: t.fin ? fmtDate(t.fin) : "-",
        comentarios_txt: t.comentarios?.trim() ? t.comentarios : "Sin comentarios",
        observaciones_txt: t.observaciones_admin || t.comentario_admin || "-",
      })),
    [ticajesFiltrados, nombreUsuario, nombreOrden]
  );

  const columns = useMemo(() => {
    const comunes = [
      { field: "usuario_txt", headerName: "Usuario", flex: 1, minWidth: 220 },
      { field: "orden_txt", headerName: "OT", flex: 1, minWidth: 260 },
      { field: "cliente", headerName: "Cliente", flex: 1, minWidth: 200 },
      { field: "centro", headerName: "Centro", flex: 1, minWidth: 200 },
      { field: "inicio_txt", headerName: "Inicio", minWidth: 170 },
      { field: "fin_txt", headerName: "Fin", minWidth: 170 },
      { field: "duracion_txt", headerName: "Duración", minWidth: 120 },
      { field: "estado", headerName: "Estado", minWidth: 160 },
      {
        field: "distancias",
        headerName: "Distancias",
        minWidth: 300,
        sortable: false,
        renderCell: (params) => {
          const t = params?.row;
          if (!t) return "-";

          const inicioOk = t.en_rango === true;
          const cierreOk = t.en_rango_cierre === true;
          const cierreUnknown = t.en_rango_cierre == null;

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                width: "100%",
                overflow: "hidden",
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  flexWrap: "nowrap",
                  whiteSpace: "nowrap",
                  overflowX: "auto",
                  overflowY: "hidden",
                  width: "100%",
                  pb: 0.25,
                  "&::-webkit-scrollbar": { height: 6 },
                }}
              >
                {t.distancia_m != null ? (
                  <Chip
                    label={`Inicio: ${t.distancia_m} m`}
                    size="small"
                    color={inicioOk ? "success" : "error"}
                    variant="filled"
                    clickable
                    onClick={() => abrirMapa(t, "inicio")}
                  />
                ) : (
                  <Chip label="Inicio: -" size="small" variant="outlined" />
                )}

                {t.distancia_m_cierre != null ? (
                  <Chip
                    label={`Cierre: ${t.distancia_m_cierre} m`}
                    size="small"
                    color={cierreUnknown ? "default" : cierreOk ? "success" : "error"}
                    variant="filled"
                    clickable
                    onClick={() => abrirMapa(t, "cierre")}
                  />
                ) : (
                  <Chip label="Cierre: -" size="small" variant="outlined" />
                )}
              </Stack>
            </Box>
          );
        },
      },
      { field: "comentarios_txt", headerName: "Comentarios", minWidth: 220, flex: 1 },
      { field: "modificado_por_nombre", headerName: "Modificado por", minWidth: 220 },
      { field: "observaciones_txt", headerName: "Observaciones", minWidth: 260, flex: 1 },
      {
        field: "acciones",
        headerName: "Acciones",
        minWidth: 360,
        sortable: false,
        renderCell: (params) => {
          const t = params?.row;

          return (
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DescriptionIcon />}
                onClick={() =>
                  navigate(`/app/misIntervenciones/${t.id}/parte`, {
                    state: {
                      readOnly: true,
                      ot: t.orden_txt || t.orden_nombre,
                      cliente: t.cliente,
                      centro: t.centro,
                      fecha: t.inicio_txt,
                    },
                  })
                }
              >
                Parte
              </Button>

              <Button
                size="small"
                variant="contained"
                startIcon={<SendIcon />}
                onClick={() => openSendModal(t)}
              >
                Enviar
              </Button>

              <Button
                size="small"
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => {
                  setTicajeEditar(t);
                  setModalEditar(true);
                }}
              >
                Editar
              </Button>
            </Stack>
          );
        },
      },
    ];

    if (esAdmin) {
      return [
        comunes[0],
        comunes[1],
        comunes[2],
        comunes[3],
        { field: "gerente_nombre", headerName: "Gerente", minWidth: 200 },
        ...comunes.slice(4),
      ];
    }

    return comunes;
  }, [esAdmin, navigate, openSendModal]);

  const usuariosOrdenados = useMemo(() => {
    return (usuariosFiltrables || [])
      .slice()
      .sort((a, b) => nombreCompleto(a).localeCompare(nombreCompleto(b)));
  }, [usuariosFiltrables]);

  const usuarioSeleccionado =
    usuariosOrdenados.find((u) => String(u.id) === String(filtroUsuario)) || null;

  const clientesOrdenados = useMemo(() => {
    return (clientes || []).slice().sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
  }, [clientes]);

  const centrosOrdenados = useMemo(() => {
    return (centros || []).slice().sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
  }, [centros]);

  const ordenesOrdenadas = useMemo(() => {
    return (ordenes || []).slice().sort((a, b) => (a.titulo || "").localeCompare(b.titulo || ""));
  }, [ordenes]);

  const titulo = esAdmin ? "Intervenciones Finalizadas" : "Intervenciones Finalizadas de mi equipo";

  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {titulo}
        </Typography>

        <Paper sx={{ p: 2 }}>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "2fr 1fr 1fr 1fr 1fr 1fr auto",
              },
              alignItems: "end",
            }}
          >
            <Autocomplete
              size="small"
              options={usuariosOrdenados}
              getOptionLabel={(u) => nombreCompleto(u)}
              isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
              value={usuarioSeleccionado}
              onChange={(_, newValue) => setFiltroUsuario(newValue ? newValue.id : "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={esAdmin ? "Usuario" : "Usuario de mi equipo"}
                  placeholder="Escribe para buscar…"
                />
              )}
              clearOnEscape
            />

            <FormControl size="small">
              <InputLabel>Cliente</InputLabel>
              <Select
                label="Cliente"
                value={filtroCliente}
                onChange={(e) => setFiltroCliente(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {clientesOrdenados.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small">
              <InputLabel>Centro</InputLabel>
              <Select
                label="Centro"
                value={filtroCentro}
                onChange={(e) => setFiltroCentro(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {centrosOrdenados.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small">
              <InputLabel>OT</InputLabel>
              <Select
                label="OT"
                value={filtroOrden}
                onChange={(e) => setFiltroOrden(e.target.value)}
              >
                <MenuItem value="">Todas</MenuItem>
                {ordenesOrdenadas.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.titulo || `OT ${o.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Desde"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
            />

            <TextField
              label="Hasta"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
            />

            <Button variant="contained" onClick={limpiarFiltros} sx={{ height: 40 }}>
              Limpiar
            </Button>
          </Box>
        </Paper>

        <Paper sx={{ height: 660, p: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            pagination
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(m) => {
              setPage(m.page);
              setPageSize(m.pageSize);
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                borderBottom: "1px solid rgba(0,0,0,0.08)",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid rgba(0,0,0,0.06)",
              },
            }}
          />
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} useFlexGap flexWrap="wrap">
            <Button
              startIcon={<DownloadIcon />}
              onClick={() =>
                exportarCSVReducido(
                  ticajes,
                  esAdmin ? "todas_intervenciones.csv" : "intervenciones_equipo.csv"
                )
              }
            >
              CSV Todas
            </Button>

            <Button
              startIcon={<DownloadIcon />}
              onClick={() =>
                exportarCSVReducido(
                  ticajesFiltrados,
                  esAdmin ? "intervenciones_filtradas.csv" : "intervenciones_equipo_filtradas.csv"
                )
              }
            >
              CSV Filtradas
            </Button>

            <Button
              startIcon={<DownloadIcon />}
              onClick={() =>
                exportarExcel(
                  ticajes,
                  esAdmin ? "todas_intervenciones.xlsx" : "intervenciones_equipo.xlsx"
                )
              }
            >
              Excel Todas
            </Button>

            <Button
              startIcon={<DownloadIcon />}
              onClick={() =>
                exportarExcel(
                  ticajesFiltrados,
                  esAdmin ? "intervenciones_filtradas.xlsx" : "intervenciones_equipo_filtradas.xlsx"
                )
              }
            >
              Excel Filtradas
            </Button>
          </Stack>
        </Paper>

        {esAdmin && (
          <Box>
            <FingerprintChecker />
          </Box>
        )}

        <Dialog
          open={modalEditar}
          onClose={() => {
            if (savingEdit) return;
            setModalEditar(false);
            setTicajeEditar(null);
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Editar intervención</DialogTitle>
          <Divider />

          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <TextField
                label="Inicio"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={editInicio}
                onChange={(e) => setEditInicio(e.target.value)}
              />

              <TextField
                label="Fin"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={editFin}
                onChange={(e) => setEditFin(e.target.value)}
              />

              <TextField
                label="Motivo fin"
                value={editMotivo}
                onChange={(e) => setEditMotivo(e.target.value)}
                placeholder="Motivo de la modificación"
              />

              <FormControl size="small">
                <InputLabel>OT</InputLabel>
                <Select
                  label="OT"
                  value={editOrdenId}
                  onChange={(e) => setEditOrdenId(e.target.value)}
                >
                  <MenuItem value="">Sin OT</MenuItem>
                  {ordenesOrdenadas.map((o) => (
                    <MenuItem key={o.id} value={String(o.id)}>
                      {o.titulo || `OT ${o.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Observaciones"
                value={editObs}
                onChange={(e) => setEditObs(e.target.value)}
                placeholder="Observaciones administrativas"
                multiline
                minRows={3}
              />

              <TextField
                label="Comentarios"
                value={editComentarios}
                onChange={(e) => setEditComentarios(e.target.value)}
                placeholder="Comentarios del usuario"
                multiline
                minRows={2}
              />

              {editError && <Typography color="error">{editError}</Typography>}
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setModalEditar(false);
                setTicajeEditar(null);
              }}
              disabled={savingEdit}
            >
              Cancelar
            </Button>

            <Button onClick={handleGuardarEdicion} disabled={savingEdit}>
              {savingEdit ? "Guardando..." : "Guardar"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={modalEmailOpen} onClose={closeSendModal} fullWidth maxWidth="sm">
          <DialogTitle>Enviar parte por email</DialogTitle>

          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="info">
                Se generará un PDF con el parte y se enviará como adjunto.
              </Alert>

              <TextField
                label="Email del cliente"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                fullWidth
                autoFocus
                placeholder="cliente@empresa.com"
                error={Boolean(emailTo) && !isEmailValid(emailTo)}
                helperText={
                  emailTo && !isEmailValid(emailTo) ? "Introduce un email válido." : " "
                }
              />

              {selectedTicajeEmail ? (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  OT: {selectedTicajeEmail?.orden_txt || selectedTicajeEmail?.orden_nombre || "-"} ·
                  Cliente: {selectedTicajeEmail?.cliente || "-"} · Centro:{" "}
                  {selectedTicajeEmail?.centro || "-"}
                </Typography>
              ) : null}
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button onClick={closeSendModal} disabled={sendingEmail}>
              Cancelar
            </Button>

            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={enviarParteEmail}
              disabled={sendingEmail || !isEmailValid(emailTo) || !selectedTicajeEmail?.id}
            >
              {sendingEmail ? "Enviando..." : "Enviar"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={mostrarMapa} onClose={() => setMostrarMapa(false)} fullWidth maxWidth="md">
          <DialogTitle>Ubicación de la intervención</DialogTitle>
          <Divider />

          <DialogContent sx={{ p: 2 }}>
            {ubicacionInicio || ubicacionCierre ? (
              <Box sx={{ width: "100%", height: 460 }}>
                <MapContainer center={mapCenter} zoom={15} style={{ height: "100%", width: "100%" }}>
                  <LayersControl position="topright">
                    <LayersControl.BaseLayer checked name="Satélite + Callejero">
                      <TileLayer
                        url={`https://api.maptiler.com/maps/hybrid/256/{z}/{x}/{y}.jpg?key=${process.env.REACT_APP_MAPTILER_KEY}&language=es`}
                        attribution='&copy; <a href="https://www.maptiler.com/copyright/">MapTiler</a>'
                      />
                    </LayersControl.BaseLayer>
                  </LayersControl>

                  {ubicacionInicio && (
                    <Marker position={[ubicacionInicio.lat, ubicacionInicio.lng]}>
                      <Tooltip direction="top" offset={[0, -10]}>
                        📍 Intervención (inicio)
                      </Tooltip>
                    </Marker>
                  )}

                  {ubicacionCierre && (
                    <Marker position={[ubicacionCierre.lat, ubicacionCierre.lng]} icon={cierreIcon}>
                      <Tooltip direction="top" offset={[0, -10]}>
                        ✅ Intervención (cierre)
                      </Tooltip>
                    </Marker>
                  )}

                  {ubicacionCentro && (
                    <Marker position={[ubicacionCentro.lat, ubicacionCentro.lng]} icon={edificioIcon}>
                      <Tooltip direction="top" offset={[0, -10]}>
                        🏢 Centro de trabajo
                      </Tooltip>
                    </Marker>
                  )}
                </MapContainer>
              </Box>
            ) : (
              <Typography>No hay coordenadas para mostrar.</Typography>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="outlined" onClick={() => setMostrarMapa(false)}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
};

export default ListadoIntervenciones;